// TestTakingScreen.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { startTest, getCurrentState, submitAnswer, completeTest } from 'api/apiHelper';
import url from 'config';

interface Question {
    id: number;
    question_text: string;
    question_image_url?: string | null;
    option_a: string;
    option_a_image_url?: string | null;
    option_b: string;
    option_b_image_url?: string | null;
    option_c: string;
    option_c_image_url?: string | null;
    option_d: string;
    option_d_image_url?: string | null;
  }

interface TestQuestion {
  id: number;
  question: Question;
  question_order: number;
  selected_option: string | null;
  answer_image_url?: string | null;
}

interface TestSession {
  id: number;
  test_token: string;
  status: string;
  started_at: string | null;
  total_questions: number;
  time_limit_minutes: number;
  questions_attempted: number;
  correct_answers: number;
  test_questions: TestQuestion[];
  time_remaining_seconds: number | null;
}

interface TestTakingScreenProps {
  onComplete?: () => void;
  testToken?: string;
}

const TestTakingScreen: React.FC<TestTakingScreenProps> = ({ onComplete, testToken: propTestToken }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [testSession, setTestSession] = useState<TestSession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(600);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState<boolean>(false);
  const [submittingAnswer, setSubmittingAnswer] = useState<boolean>(false);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [modalImage, setModalImage] = useState<string>('');
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (testToken) {
      initializeTest();
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [testToken]);

  useEffect(() => {
    if (testSession && testSession.status === 'in_progress') {
      startTimer();
    }
  }, [testSession]);

  const initializeTest = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('test token:', testToken);
      const stateResponse = await getCurrentState(testToken!);
      console.log('Current test state:', stateResponse.data);
      
      if (stateResponse.data.status === 'pending') {
        const startResponse = await startTest(testToken!);
        console.log('Test started:', startResponse.data);
        setTestSession(startResponse.data);
        setTimeRemaining(startResponse.data.time_limit_minutes * 60);
        
        const initialAnswers: Record<number, string> = {};
        startResponse.data.test_questions.forEach((tq: TestQuestion) => {
          if (tq.selected_option) {
            initialAnswers[tq.question_order] = tq.selected_option;
          }
        });
        setAnswers(initialAnswers);
        
        toast.success('Test started! Good luck!');
        
      } else if (stateResponse.data.status === 'in_progress') {
        setTestSession(stateResponse.data);
        setTimeRemaining(stateResponse.data.time_remaining_seconds || 0);
        
        const existingAnswers: Record<number, string> = {};
        stateResponse.data.test_questions.forEach((tq: TestQuestion) => {
          if (tq.selected_option) {
            existingAnswers[tq.question_order] = tq.selected_option;
          }
        });
        setAnswers(existingAnswers);
        const hasExistingAnswers = Object.keys(existingAnswers).length > 0;
        const startedAt = stateResponse.data.started_at ? new Date(stateResponse.data.started_at) : null;
        const startedMs = startedAt ? Date.now() - startedAt.getTime() : 0;
        const shouldShowResumeToast = hasExistingAnswers || startedMs > 60 * 1000;

        if (shouldShowResumeToast) {
          toast.info('Resuming your test...');
        }
        
      } else if (stateResponse.data.status === 'completed') {
        toast.info('Test already completed. Thank you!');
        navigate(`/test-thank-you/${testToken}`);
        return;
        
      } else if (stateResponse.data.status === 'expired') {
        setError('Test link has expired');
        toast.error('Test link has expired');
        return;
      }
      
    } catch (error: any) {
      console.error('Test initialization error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load test';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = async (option: string) => {
    if (!testSession || submittingAnswer) return;
    
    const currentQuestion = testSession.test_questions[currentQuestionIndex];
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.question_order]: option
    }));
    
    // Submit answer to backend
    setSubmittingAnswer(true);
    try {
      const response = await submitAnswer(testToken!, {
        test_question_id: currentQuestion.id,
        selected_option: option
      });
      
      console.log('Answer submitted:', response.data);
      
      setTestSession(prev => prev ? {
        ...prev,
        questions_attempted: response.data.questions_attempted,
        correct_answers: response.data.correct_answers
      } : null);
      
    } catch (error: any) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to save answer. Please try again.');
      
      setAnswers(prev => {
        const newAnswers = { ...prev };
        delete newAnswers[currentQuestion.question_order];
        return newAnswers;
      });
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleNext = () => {
    if (testSession && currentQuestionIndex < testSession.test_questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleQuestionJump = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const handleAutoSubmit = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    toast.warning('Time is up! Auto-submitting your test...');
    await handleSubmitTest();
  };

  const handleSubmitTest = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await completeTest(testToken!);
      console.log('Test completed:', response.data);
      
      toast.success('Test submitted successfully!');
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // In unified flow, navigate to thank you screen. Otherwise navigate to thank you
      if (onComplete) {
        // Unified flow - navigate to thank you screen which will have continue button
        onComplete();
      } else {
        navigate(`/test-thank-you/${testToken}`);
      }
      
    } catch (error: any) {
      console.error('Error submitting test:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to submit test';
      toast.error(errorMessage);
      setIsSubmitting(false);
      setShowConfirmSubmit(false);
    }
  };

  const handleViewImage = (imageUrl: string) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  const handleImageError = (imageUrl: string, location: string) => {
    console.error(`Failed to load image at ${location}:`, imageUrl);
    console.error('Full URL attempted:', getImageUrl(imageUrl));
    setImageLoadErrors(prev => ({ ...prev, [imageUrl]: true }));
  };

  const getImageUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    
    // If it's already a full URL, return it
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // Use your existing API base URL
    const API_BASE_URL = url.app.BACKEND_URL;
    
    // Remove trailing slash from API_BASE_URL and ensure path starts with /
    const baseUrl = API_BASE_URL.replace(/\/$/, '');
    const path = imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl;
    
    const fullUrl = `${baseUrl}${path}`;
    
    // Debug log to see what URLs are being generated
    console.log('Image URL:', imageUrl, '→', fullUrl);
    
    return fullUrl;
  };

  if (loading) {
    return <Backdrop message="Loading test..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Error</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  if (!testSession) {
    return null;
  }

  const currentQuestion = testSession.test_questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const isTimeWarning = timeRemaining <= 120;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Timer */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-800">Assessment Test</h1>
              <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {testSession.test_questions.length}</p>
            </div>
            <div className={`flex items-center px-4 py-2 rounded-lg ${isTimeWarning ? 'bg-red-100' : 'bg-indigo-100'}`}>
              <Clock className={`w-5 h-5 mr-2 ${isTimeWarning ? 'text-red-600' : 'text-brand-500'}`} />
              <span className={`font-mono text-lg font-semibold ${isTimeWarning ? 'text-red-600' : 'text-brand-500'}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-3">Questions</h3>
              <div className="grid grid-cols-5 lg:grid-cols-5 gap-2">
                {testSession.test_questions.map((q, index) => (
                  <button
                    key={q.id}
                    onClick={() => handleQuestionJump(index)}
                    disabled={submittingAnswer}
                    className={`
                      w-10 h-10 rounded-lg font-semibold transition-all
                      ${currentQuestionIndex === index 
                        ? 'bg-brand-500 text-white ring-2 ring-indigo-300' 
                        : answers[q.question_order]
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Answered:</span>
                  <span className="font-semibold text-gray-800">{answeredCount}/{testSession.test_questions.length}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Remaining:</span>
                  <span className="font-semibold text-gray-800">{testSession.test_questions.length - answeredCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 select-none">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                    Question {currentQuestion.question_order}
                  </span>
                  {answers[currentQuestion.question_order] && (
                    <span className="inline-flex items-center text-sm text-green-600">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Answered
                    </span>
                  )}
                </div>
                
                {/* Question Text */}
                <h2 className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed mb-4 select-none">
                  {currentQuestion.question.question_text}
                </h2>

                {/* Question Image */}
                {currentQuestion.question.question_image_url && (
                  <div className="mb-6">
                    <div className="relative rounded-lg overflow-hidden">
                      <img
                        src={getImageUrl(currentQuestion.question.question_image_url) || ''}
                        alt="Question"
                        onClick={() => handleViewImage(getImageUrl(currentQuestion.question.question_image_url) || '')}
                        onError={() => handleImageError(currentQuestion.question.question_image_url!, 'question')}
                        className="max-w-full h-auto rounded-lg border-2 border-gray-200 cursor-pointer hover:border-indigo-400 transition-all"
                        onLoad={() => console.log('✓ Question image loaded successfully')}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <ImageIcon className="w-3 h-3 mr-1" />
                      Click to enlarge
                    </p>
                  </div>
                )}
              </div>

            {/* Options */}
            <div className="space-y-3 mb-8">
                {['A', 'B', 'C', 'D'].map((option) => {
                    const optionKey = `option_${option.toLowerCase()}` as keyof Question;
                    const optionImageKey = `option_${option.toLowerCase()}_image_url` as keyof Question;
                    const isSelected = answers[currentQuestion.question_order] === option;
                    const optionImageUrl = currentQuestion.question[optionImageKey] as string | null | undefined;
                    const hasImage = optionImageUrl && optionImageUrl.trim() !== '';
                    
                    return (
                    <button
                        key={option}
                        onClick={() => handleAnswerSelect(option)}
                        disabled={submittingAnswer}
                        className={`
                        w-full text-left p-4 rounded-lg border-2 transition-all
                        ${isSelected 
                            ? 'border-brand-500 bg-indigo-50 ring-2 ring-indigo-200' 
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                        }
                        ${submittingAnswer ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        <div className="flex items-start">
                        <span className={`
                            flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold mr-3
                            ${isSelected 
                            ? 'bg-brand-500 text-white' 
                            : 'bg-gray-200 text-gray-700'
                            }
                        `}>
                            {option}
                        </span>
                        <div className="flex-1">
                            {/* Option Text */}
                            {currentQuestion.question[optionKey] && (
                            <span className="text-gray-800 pt-1 block mb-2 select-none">
                                {currentQuestion.question[optionKey]}
                            </span>
                            )}
                            
                            {/* Option Image */}
                            {hasImage && (
                              <div className="mt-2">
                                <img
                                  src={getImageUrl(optionImageUrl) || ''}
                                  alt={`Option ${option}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewImage(getImageUrl(optionImageUrl) || '');
                                  }}
                                  onError={() => handleImageError(optionImageUrl, `option ${option}`)}
                                  onLoad={() => console.log(`✓ Option ${option} image loaded`)}
                                  className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300 cursor-pointer hover:border-indigo-400 transition-all"
                                />
                              </div>
                            )}
                        </div>
                        </div>
                    </button>
                    );
                })}
                </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-6 border-t">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </button>

                <div className="flex gap-3">
                  {currentQuestionIndex === testSession.test_questions.length - 1 ? (
                    <button
                      onClick={() => setShowConfirmSubmit(true)}
                      disabled={isSubmitting}
                      className="flex items-center px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Test'}
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      className="flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all"
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Unanswered Questions Warning */}
            {currentQuestionIndex === testSession.test_questions.length - 1 && answeredCount < testSession.test_questions.length && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-1">Unanswered Questions</h4>
                    <p className="text-sm text-yellow-700">
                      You have {testSession.test_questions.length - answeredCount} unanswered question(s). 
                      Please review your answers before submitting.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image View Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={modalImage}
              alt="Full size"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = ''; // Clear src on error
              }}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Submit Test?</h3>
            <p className="text-gray-600 mb-2">
              You have answered {answeredCount} out of {testSession.test_questions.length} questions.
            </p>
            {answeredCount < testSession.test_questions.length && (
              <p className="text-yellow-700 text-sm mb-4">
                {testSession.test_questions.length - answeredCount} question(s) remain unanswered and will be marked as incorrect.
              </p>
            )}
            <p className="text-gray-600 mb-6">
              Once submitted, you cannot change your answers. Are you sure you want to submit?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
                disabled={isSubmitting}
              >
                Review Again
              </button>
              <button
                onClick={handleSubmitTest}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestTakingScreen;